import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If an account exists for this address, a reset link has been sent.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      window.history.replaceState({}, "", "/command-center/reset-password");
      toast.success("Password reset. Please sign in.");
      nav("/command-center/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inputCls = "mt-1.5 w-full bg-[#121212] border border-zinc-800 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-500 font-mono-data";

  return (
    <div className="dark min-h-screen bg-[#0A0A0A] text-zinc-100 font-ui flex items-center justify-center px-6">
      <meta name="referrer" content="no-referrer" />
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <div className="flex items-center gap-2 text-emerald-400"><ShieldCheck className="h-5 w-5" /><span className="label-caps">Account Recovery</span></div>
        {token ? (
          <>
            <h1 className="mt-4 font-tactical font-black text-2xl">Set a new password</h1>
            <form onSubmit={doReset} className="mt-6 space-y-4">
              <div>
                <label className="label-caps text-zinc-500">New password (min 12 chars)</label>
                <input data-testid="reset-new-password" type="password" required minLength={12} className={inputCls} value={pw} onChange={(e) => setPw(e.target.value)} />
              </div>
              <button data-testid="reset-submit-btn" type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm py-2.5 text-sm font-semibold disabled:opacity-50">Reset password</button>
            </form>
          </>
        ) : (
          <>
            <h1 className="mt-4 font-tactical font-black text-2xl">Reset your password</h1>
            <p className="mt-2 text-sm text-zinc-500">We'll email a reset link if an account exists.</p>
            <form onSubmit={requestReset} className="mt-6 space-y-4">
              <div>
                <label className="label-caps text-zinc-500">Email</label>
                <input data-testid="forgot-email-input" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm py-2.5 text-sm font-semibold disabled:opacity-50">Send reset link</button>
            </form>
          </>
        )}
        <Link to="/command-center/login" className="mt-8 block text-center text-xs text-zinc-600 hover:text-zinc-400">← Back to sign in</Link>
      </div>
    </div>
  );
}
